/**
 * CP Mapping - Edit — Access `frmReportCashFlowQryCPMapping_result_add`.
 *
 * The selected ERP customer/vender (read-only) is linked to a MISA object;
 * Save writes the chosen `misa_id` onto `cp_vn.misa_code`.
 */
import { Autocomplete, Box, Dialog, DialogContent, TextField } from '@mui/material'
import DialogTitleBar from '../../../../components/DialogTitleBar'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetCashflowLookupQuery,
  useSaveCashflowMapCpMutation,
  type MapCp,
} from '../../../../store/api/reportCashflowApi'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { FORM_GRID_2COL } from '../../../../theme/responsive'
import { readonlyFieldSx } from './formHelpers'

interface Props {
  open: boolean
  row: Record<string, unknown> | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

export default function CpMappingEditDialog({ open, row, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const cp = (row ?? {}) as Partial<MapCp>
  const [misa, setMisa] = useState<{ value: string; label: string } | null>(null)
  const [misaInput, setMisaInput] = useState('')
  const [save, { isLoading }] = useSaveCashflowMapCpMutation()

  const { data: misaLookup } = useGetCashflowLookupQuery({ kind: 'misa-object', q: misaInput || undefined }, { skip: !open })
  const seededMisa = useMemo(
    () => (cp.misaId ? { value: String(cp.misaId), label: `${cp.misaCode ?? ''} - ${cp.misaName ?? ''}` } : null),
    [cp.misaId, cp.misaCode, cp.misaName],
  )
  const options = useMemo(() => {
    const items = misaLookup?.items ?? []
    if (seededMisa && !items.some((i) => i.value === seededMisa.value)) return [seededMisa, ...items]
    return items
  }, [misaLookup, seededMisa])

  useEffect(() => {
    if (!open) return
    setMisa(seededMisa)
    setMisaInput('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleSave = async () => {
    if (!canEdit) return
    if (!cp.erpCode) {
      window.alert('Please choose CP VN!')
      return
    }
    try {
      await save({ cpCode: String(cp.erpCode), misaId: misa?.value ?? null }).unwrap()
      snack.success('Successfully Updated')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not save the mapping.')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleBar
        title="CP Mapping - Edit"
        onSave={handleSave}
        onCancel={onClose}
        saving={isLoading}
        saveDisabled={isLoading || !canEdit || !cp.erpCode}
      />
      <DialogContent>
        <Box sx={{ ...FORM_GRID_2COL, mt: 0.5 }}>
          <TextField label="ERP Code" value={cp.erpCode ?? ''} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <TextField label="ERP Name" value={cp.erpName ?? ''} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <TextField label="ERP VAT Code" value={cp.erpVat ?? ''} size="small" fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
          <Box />
          <Autocomplete
            size="small"
            options={options}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            value={misa}
            onChange={(_, v) => setMisa(v)}
            inputValue={misaInput}
            onInputChange={(_, v) => setMisaInput(v)}
            sx={{ gridColumn: '1 / -1' }}
            renderInput={(p) => <TextField {...p} label="MISA - Vender/Customer" InputLabelProps={{ shrink: true }} />}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
